import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import { listVirtualOfficeSparks } from "../../api/client";
import {
  SPARK_MAX_PER_VISIT,
  SPARK_SHOW_MS,
  SPARK_WINDOW_MS,
  readSeenSparkWindow,
  readShownSparkIds,
  shouldRevealSpark,
  sparkForWindow,
  sparkWindowId,
  unwrapSparks,
  writeSeenSparkWindow,
  writeShownSparkIds,
} from "./voFloorSparks";

export function useVoFloorSparks({ enabled = false, roomId = 0, onShow } = {}) {
  const voId = Number(roomId) || 0;
  const joinedAtRef = useRef(0);
  const visibleWindowRef = useRef(null);
  const [spark, setSpark] = useState(null);
  const [dismissedWindow, setDismissedWindow] = useState(null);
  const { data } = useQuery(["vo-floor-sparks", voId], () => listVirtualOfficeSparks(voId), {
    enabled: Boolean(enabled && voId),
    staleTime: 5 * 60_000,
    refetchInterval: enabled ? 30 * 60_000 : false,
  });
  const sparks = useMemo(() => unwrapSparks(data), [data]);
  const onShowRef = useRef(onShow);
  onShowRef.current = onShow;

  useEffect(() => {
    if (!enabled) return undefined;
    if (!joinedAtRef.current) joinedAtRef.current = Date.now();
    const storage = typeof window !== "undefined" ? window.sessionStorage : null;
    const tick = () => {
      const now = Date.now();
      if (visibleWindowRef.current != null && now % SPARK_WINDOW_MS > SPARK_SHOW_MS) {
        visibleWindowRef.current = null;
        setSpark(null);
      }
      if (!sparks.length) return;
      const hidden = typeof document !== "undefined" && document.hidden;
      const lastWindow = readSeenSparkWindow(storage);
      const seen = readShownSparkIds(storage);
      const next = shouldRevealSpark({
        joinedAt: joinedAtRef.current,
        now,
        lastWindow,
        dismissedWindow,
        hidden,
        shownCount: seen.length,
      });
      if (!next) return;
      const pick = sparkForWindow(sparks, now, { seenIds: seen });
      if (!pick) return;
      const win = sparkWindowId(now);
      if (visibleWindowRef.current === win) return;
      visibleWindowRef.current = win;
      writeSeenSparkWindow(storage, win);
      writeShownSparkIds(storage, [...seen, String(pick.id)]);
      setSpark(pick);
      try {
        onShowRef.current?.(pick);
      } catch {
        /* ignore */
      }
    };
    tick();
    const id = window.setInterval(tick, 4000);
    return () => window.clearInterval(id);
  }, [enabled, dismissedWindow, sparks]);

  const dismiss = () => {
    const win = sparkWindowId();
    setDismissedWindow(win);
    visibleWindowRef.current = null;
    setSpark(null);
  };

  return {
    spark,
    visible: Boolean(spark),
    dismiss,
    sparks,
    remaining: Math.max(0, SPARK_MAX_PER_VISIT - readShownSparkIds(
      typeof window !== "undefined" ? window.sessionStorage : null
    ).length),
  };
}
